import React from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import ComparisonTable from '@components/shared/ComparisonTable';
import { PageSlice } from '@contracts';
import { TableData } from '@data/tablesData';

interface Props {
  slice: PageSlice;
  data: TableData;
}

const ComparisonTableAndImage: React.FC<Props> = ({ slice, data }) => {
  const { t } = useTranslation();
  const image = getImage(slice.primary?.image);

  return (
    <Container $backgroundImage={slice.primary?.background_image.url}>
      <LeftColumn>
        <TitleWrap>{slice.primary?.table_title.text.toUpperCase()}</TitleWrap>
        <ComparisonTable slice={slice} data={data} />
        {slice.primary?.has_prices && <AsOf>{t('components.prices-as-of')}</AsOf>}
      </LeftColumn>
      <RightColumn>
        {slice.primary?.image && (
          <ImageWrap>
            <GatsbyImage image={image} alt={slice.primary?.image?.alt || ''} objectFit="contain" />
          </ImageWrap>
        )}
      </RightColumn>
    </Container>
  );
};

export default ComparisonTableAndImage;

interface StyleProps {
  $backgroundImage: string;
}

const Container = styled.div<StyleProps>`
  display: flex;
  justify-content: center;

  background: ${(props) => `url(${props.$backgroundImage}) right bottom, linear-gradient(to top, #EBF1F5, #FFFBE7);`};
  background-repeat: no-repeat;

  @media (max-width: 1220px) {
    flex-direction: column-reverse;
    background: ${(props) => `url(${props.$backgroundImage}), #EBF1F5;`};
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;

  flex: 2;
  margin-left: 223px;

  @media (max-width: 1220px) {
    flex: 1;
    margin-left: 0;
  }

  @media (min-width: 1441px) {
    align-items: center;
    justify-content: center;
  }
`;

const TitleWrap = styled.div`
  color: ${Colors.navyBlue};
  margin-bottom: 40px;
  font-family: BrownStd;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.1em;
  text-align: left;
  margin-top: 60px;

  @media (max-width: 1220px) {
    padding-top: 30px;
    margin-inline: 105px;
    margin-top: 0;

    font-size: 14px;
    line-height: 20px;
    letter-spacing: 1px;
    text-align: center;
  }
  @media (max-width: 500px) {
    margin-inline: 35px;
  }
`;

const RightColumn = styled.div`
  display: flex;
  justify-content: end;
  margin-top: 10px;

  @media (max-width: 1220px) {
    display: none;
  }

  @media (min-width: 1220px) {
    margin-top: 52px;
  }
`;

const ImageWrap = styled.div`
  display: flex;
  justify-content: end;
  align-items: flex-end;
  overflow: hidden;

  @media (max-width: 1220px) {
    width: 80vw;
  }

  @media (min-width: 1220px) {
    img {
      width: 450px;
      object-fit: cover;
      margin-top: auto;
      transform: rotate(10deg) translate(40px, 0);
    }
  }
`;

const AsOf = styled.div`
  width: 580px;
  font-family: BrownStd;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  text-align: end;
  letter-spacing: 0;
  color: ${Colors.navyBlue}80;
  padding: 20px 0;

  @media (max-width: 1024px) {
    width: auto;
    text-align: center;
  }
`;
